import React, { useState, useEffect } from "react";
import { useNavigate, useParams, Link } from "react-router-dom";
import { Plus, X, ArrowRight, ArrowLeft, Check, Briefcase } from "lucide-react";
import { http, formatErr } from "../../lib/api";
import { useAuth } from "../../context/AuthContext";
import { toast } from "sonner";

const STEPS = ["Basic Info", "Services", "Location & Contact", "Media & Tags", "Review & Submit"];

function Field({ label, required, hint, children }) {
  return (
    <div>
      <label className="block text-sm font-semibold text-gray-700 mb-1.5">{label}{required && <span className="text-red-500 ml-0.5">*</span>}</label>
      {children}
      {hint && <div className="text-xs text-gray-400 mt-1">{hint}</div>}
    </div>
  );
}

function Inp({ ...props }) {
  return <input {...props} className={`w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none focus:border-yellow-400 transition-colors ${props.className || ""}`} />;
}

export default function CreateListing() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const { id: editId } = useParams();
  const isEdit = !!editId;

  const [step, setStep] = useState(0);
  const [categories, setCategories] = useState([]);
  const [submitting, setSubmitting] = useState(false);
  const [errors, setErrors] = useState({});

  const [form, setForm] = useState({
    name: "", tagline: "", description: "", category_id: "",
    services: [{ name: "", description: "", price: "" }],
    location: "", city: "", country: "", address: "",
    phone: "", email: "", website: "", whatsapp: "", availability: "", response_time: "",
    logo_url: "", cover_image: "", gallery_urls: "",
    tags: "",
    terms_accepted: false,
  });

  useEffect(() => {
    if (!user) { navigate("/auth", { replace: true }); return; }
    http.get("/marketplace/categories").then(r => setCategories(r.data)).catch(() => {});
    if (isEdit) {
      http.get(`/marketplace/listings/${editId}`).then(r => {
        const d = r.data;
        setForm({
          ...d,
          services: d.service_list?.length > 0 ? d.service_list : [{ name: "", description: "", price: "" }],
          tags: (d.tags || []).join(", "),
          gallery_urls: (d.gallery || []).join("\n"),
        });
      }).catch(() => toast.error("Failed to load listing"));
    }
  }, [user, navigate, editId, isEdit]);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));

  const addService = () => set("services", [...form.services, { name: "", description: "", price: "" }]);
  const updateService = (i, k, v) => set("services", form.services.map((s, idx) => idx === i ? { ...s, [k]: v } : s));
  const removeService = (i) => set("services", form.services.filter((_, idx) => idx !== i));

  const validate = () => {
    const e = {};
    if (!form.name.trim()) e.name = "Business name is required";
    if (!form.category_id) e.category_id = "Please select a category";
    if (!form.description.trim()) e.description = "Description is required";
    if (!form.phone && !form.email && !form.whatsapp) e.contact = "At least one contact method is required";
    if (step === 4 && !form.terms_accepted) e.terms = "Please accept the terms";
    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const next = () => {
    if (step === 0 && (!form.name.trim() || !form.category_id || !form.description.trim())) {
      const e = {};
      if (!form.name.trim()) e.name = "Required";
      if (!form.category_id) e.category_id = "Required";
      if (!form.description.trim()) e.description = "Required";
      setErrors(e);
      return;
    }
    if (step === 2 && !form.phone && !form.email && !form.whatsapp) {
      setErrors({ contact: "At least one contact method is required" });
      return;
    }
    setErrors({});
    setStep(s => Math.min(s + 1, STEPS.length - 1));
  };

  const submit = async () => {
    if (!form.terms_accepted) { toast.error("Please accept the terms to continue"); return; }
    setSubmitting(true);
    try {
      const payload = {
        ...form,
        tags: form.tags.split(",").map(t => t.trim()).filter(Boolean),
        gallery: form.gallery_urls.split("\n").map(u => u.trim()).filter(Boolean),
        service_list: form.services.filter(s => s.name.trim()),
      };
      let r;
      if (isEdit) r = await http.put(`/marketplace/listings/${editId}`, payload);
      else r = await http.post("/marketplace/listings", payload);
      toast.success(isEdit ? "Listing updated!" : "Listing submitted for review!");
      navigate("/marketplace/my-listings");
    } catch (e) { toast.error(formatErr(e)); }
    finally { setSubmitting(false); }
  };

  if (!user) return null;

  return (
    <div className="max-w-2xl mx-auto space-y-8">
      <div>
        <Link to="/marketplace" className="inline-flex items-center gap-1.5 text-sm text-gray-500 hover:text-yellow-600 mb-4 block"><ArrowLeft size={14} /> Marketplace</Link>
        <h1 className="text-2xl font-bold text-gray-900" style={{ fontFamily: "Cormorant Garamond, serif" }}>
          {isEdit ? "Edit Your Listing" : "Create a Business Listing"}
        </h1>
        <p className="text-gray-500 text-sm mt-1">Showcase your skills to the Celestial community. Listings are reviewed before going live.</p>
      </div>

      {/* Step indicator */}
      <div className="flex items-center gap-1 overflow-x-auto scrollbar-none pb-2">
        {STEPS.map((s, i) => (
          <React.Fragment key={s}>
            <div className={`flex items-center gap-2 px-3 py-2 rounded-xl text-xs font-semibold whitespace-nowrap shrink-0 transition-colors ${i === step ? "text-white" : i < step ? "text-green-700 bg-green-50" : "text-gray-400 bg-gray-100"}`} style={i === step ? { background: "#0F1E38" } : {}}>
              {i < step ? <Check size={12} /> : <span>{i + 1}</span>}
              {s}
            </div>
            {i < STEPS.length - 1 && <div className={`w-4 h-0.5 shrink-0 rounded ${i < step ? "bg-green-300" : "bg-gray-200"}`} />}
          </React.Fragment>
        ))}
      </div>

      {/* Step content */}
      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6 space-y-5">
        {step === 0 && (
          <>
            <h2 className="font-bold text-gray-900 text-lg">Basic Information</h2>
            <Field label="Business / Service Name" required>
              <Inp value={form.name} onChange={e => set("name", e.target.value)} placeholder="e.g. Grace Catering Services" />
              {errors.name && <div className="text-xs text-red-500 mt-1">{errors.name}</div>}
            </Field>
            <Field label="Category" required>
              <select value={form.category_id} onChange={e => set("category_id", e.target.value)} className={`w-full px-4 py-3 border rounded-xl text-sm outline-none bg-white ${errors.category_id ? "border-red-300" : "border-gray-200 focus:border-yellow-400"}`}>
                <option value="">Select a category…</option>
                {categories.map(c => <option key={c.id} value={c.id}>{c.icon ? `${c.icon} ` : ""}{c.name}</option>)}
              </select>
              {errors.category_id && <div className="text-xs text-red-500 mt-1">{errors.category_id}</div>}
            </Field>
            <Field label="Tagline" hint="A one-line summary of your service (max 120 chars)">
              <Inp value={form.tagline} onChange={e => set("tagline", e.target.value)} maxLength={120} placeholder="Professional plumbing services with 10+ years experience" />
            </Field>
            <Field label="Full Description" required hint="Describe your business, experience, and what makes you stand out.">
              <textarea value={form.description} onChange={e => set("description", e.target.value)} rows={5} className={`w-full px-4 py-3 border rounded-xl text-sm outline-none resize-none focus:border-yellow-400 transition-colors ${errors.description ? "border-red-300" : "border-gray-200"}`} placeholder="Tell customers about your business…" />
              {errors.description && <div className="text-xs text-red-500 mt-1">{errors.description}</div>}
            </Field>
          </>
        )}

        {step === 1 && (
          <>
            <h2 className="font-bold text-gray-900 text-lg">Services Offered</h2>
            <div className="space-y-4">
              {form.services.map((s, i) => (
                <div key={i} className="p-4 bg-gray-50 rounded-xl space-y-3 relative">
                  <button onClick={() => removeService(i)} className="absolute top-3 right-3 p-1 text-gray-400 hover:text-red-500 transition-colors"><X size={14} /></button>
                  <Field label={`Service ${i + 1}`} required={i === 0}>
                    <Inp value={s.name} onChange={e => updateService(i, "name", e.target.value)} placeholder="e.g. Pipe installation, Event catering…" />
                  </Field>
                  <div className="grid grid-cols-2 gap-3">
                    <Field label="Description">
                      <Inp value={s.description} onChange={e => updateService(i, "description", e.target.value)} placeholder="Brief description" />
                    </Field>
                    <Field label="Price / Rate" hint="e.g. $50/hr or From $200">
                      <Inp value={s.price} onChange={e => updateService(i, "price", e.target.value)} placeholder="$50/hr" />
                    </Field>
                  </div>
                </div>
              ))}
              <button onClick={addService} className="w-full py-3 border-2 border-dashed border-gray-200 rounded-xl text-sm font-semibold text-gray-500 hover:border-yellow-300 hover:text-yellow-600 transition-colors flex items-center justify-center gap-2">
                <Plus size={15} /> Add Another Service
              </button>
            </div>
          </>
        )}

        {step === 2 && (
          <>
            <h2 className="font-bold text-gray-900 text-lg">Location & Contact</h2>
            <div className="grid grid-cols-2 gap-4">
              <Field label="City"><Inp value={form.city} onChange={e => set("city", e.target.value)} placeholder="Lagos" /></Field>
              <Field label="Country"><Inp value={form.country} onChange={e => set("country", e.target.value)} placeholder="Nigeria" /></Field>
            </div>
            <Field label="Service Area / Location Display" hint="What location will customers see on your listing?">
              <Inp value={form.location} onChange={e => set("location", e.target.value)} placeholder="Lagos, Nigeria · Nationwide delivery" />
            </Field>
            <Field label="Full Address (optional)">
              <Inp value={form.address} onChange={e => set("address", e.target.value)} placeholder="123 Church Street, Surulere, Lagos" />
            </Field>
            {errors.contact && <div className="text-xs text-red-500">{errors.contact}</div>}
            <div className="grid grid-cols-2 gap-4">
              <Field label="Phone Number"><Inp value={form.phone} onChange={e => set("phone", e.target.value)} placeholder="+234 800 000 0000" /></Field>
              <Field label="WhatsApp Number"><Inp value={form.whatsapp} onChange={e => set("whatsapp", e.target.value)} placeholder="+234 800 000 0000" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Email"><Inp type="email" value={form.email} onChange={e => set("email", e.target.value)} placeholder="business@email.com" /></Field>
              <Field label="Website"><Inp value={form.website} onChange={e => set("website", e.target.value)} placeholder="https://…" /></Field>
            </div>
            <div className="grid grid-cols-2 gap-4">
              <Field label="Availability" hint="e.g. Mon–Fri 9am–6pm"><Inp value={form.availability} onChange={e => set("availability", e.target.value)} placeholder="Mon–Sat, 8am–6pm" /></Field>
              <Field label="Response Time"><Inp value={form.response_time} onChange={e => set("response_time", e.target.value)} placeholder="Within 2 hours" /></Field>
            </div>
          </>
        )}

        {step === 3 && (
          <>
            <h2 className="font-bold text-gray-900 text-lg">Media & Tags</h2>
            <Field label="Logo / Profile Photo URL" hint="Square image recommended (e.g. from Cloudflare R2 or any CDN)">
              <Inp value={form.logo_url} onChange={e => set("logo_url", e.target.value)} placeholder="https://…" />
            </Field>
            <Field label="Cover Photo URL" hint="Banner/cover image for your listing page (16:9 recommended)">
              <Inp value={form.cover_image} onChange={e => set("cover_image", e.target.value)} placeholder="https://…" />
            </Field>
            <Field label="Gallery Images (one URL per line)" hint="Up to 10 images showing your work">
              <textarea value={form.gallery_urls} onChange={e => set("gallery_urls", e.target.value)} rows={4} className="w-full px-4 py-3 border border-gray-200 rounded-xl text-sm outline-none font-mono resize-none" placeholder="https://image1.jpg&#10;https://image2.jpg" />
            </Field>
            <Field label="Tags" hint="Comma-separated keywords to help people find you">
              <Inp value={form.tags} onChange={e => set("tags", e.target.value)} placeholder="plumbing, repairs, Lagos, emergency call-out" />
            </Field>
          </>
        )}

        {step === 4 && (
          <>
            <h2 className="font-bold text-gray-900 text-lg">Review & Submit</h2>
            <div className="bg-gray-50 rounded-xl p-4 space-y-2 text-sm">
              <div className="flex justify-between"><span className="text-gray-500">Business Name</span><span className="font-semibold text-gray-900">{form.name}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Category</span><span className="font-semibold text-gray-900">{categories.find(c => c.id === form.category_id)?.name || "—"}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Location</span><span className="font-semibold text-gray-900">{form.location || form.city || "—"}</span></div>
              <div className="flex justify-between"><span className="text-gray-500">Services</span><span className="font-semibold text-gray-900">{form.services.filter(s => s.name).length}</span></div>
            </div>
            <div className="bg-yellow-50 border border-yellow-100 rounded-xl p-4 text-sm text-yellow-800">
              <strong>Review Policy:</strong> Your listing will be reviewed by our moderation team before going live. This typically takes 24–48 hours. You'll be notified by email.
            </div>
            <label className="flex items-start gap-3 cursor-pointer">
              <input type="checkbox" checked={form.terms_accepted} onChange={e => set("terms_accepted", e.target.checked)} className="accent-yellow-500 mt-0.5 w-4 h-4 shrink-0" />
              <span className="text-sm text-gray-700">I confirm this listing is accurate, represents a genuine service, and complies with the CelestialPeopleMeet community standards.</span>
            </label>
            {errors.terms && <div className="text-xs text-red-500">{errors.terms}</div>}
          </>
        )}
      </div>

      {/* Navigation */}
      <div className="flex gap-3">
        {step > 0 && (
          <button onClick={() => setStep(s => s - 1)} className="flex-1 py-3.5 rounded-2xl border border-gray-200 font-bold text-sm text-gray-700 hover:bg-gray-50 flex items-center justify-center gap-2">
            <ArrowLeft size={16} /> Back
          </button>
        )}
        {step < STEPS.length - 1 ? (
          <button onClick={next} className="flex-1 py-3.5 rounded-2xl font-bold text-white text-sm flex items-center justify-center gap-2" style={{ background: "#0F1E38" }}>
            Continue <ArrowRight size={16} />
          </button>
        ) : (
          <button onClick={submit} disabled={submitting} className="flex-1 py-3.5 rounded-2xl font-bold text-white text-sm flex items-center justify-center gap-2 disabled:opacity-50" style={{ background: "#C5A028" }}>
            <Briefcase size={16} />{submitting ? "Submitting…" : isEdit ? "Save Changes" : "Submit Listing"}
          </button>
        )}
      </div>
    </div>
  );
}
